<script setup lang="ts">
import { computed, type PropType } from 'vue'

import type { RouteLocationRaw } from 'vue-router'
import type { ButtonSize, ButtonType } from '@/types/components.interfaces'

const props = defineProps({
  variant: {
    type: String as PropType<ButtonType>,
    required: false,
    default: 'primary',
  },
  size: {
    type: String as PropType<ButtonSize>,
    required: false,
    default: 'md',
  },
  fluid: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  leftIcon: {
    type: String,
    required: false,
    default: '',
  },
  rightIcon: {
    type: String,
    required: false,
    default: '',
  },
  to: {
    type: [Object, String] as PropType<RouteLocationRaw>,
    required: false,
    default: undefined,
  },
  href: {
    type: String,
    required: false,
    default: undefined,
  },
  focus: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: String,
    default: '',
  },
  tooltipPlacement: {
    type: String,
    default: 'auto',
  },
})

defineEmits(['click'])

const classes = computed<(string | object)[]>(() => [
  {
    'ui-button--focus': props.focus,
    'ui-button--disabled': props.disabled,
  },
  `ui-button--${props.variant}`,
])
</script>

<template>
  <template v-if="tooltip !== ''">
    <UiTooltip
      :content="tooltip"
      :placement="tooltipPlacement"
      class="ui-tooltip-button"
    >
      <PuikButton
        v-bind="{ ...props, disabled: false }"
        @click="!props.disabled ? $emit('click') : () => {}"
        class="text-center h-full"
        :class="classes"
      >
        <slot />
      </PuikButton>
    </UiTooltip>
  </template>
  <PuikButton
    v-else
    v-bind="props"
    @click="$emit('click')"
    class="text-center"
    :class="classes"
  >
    <slot />
  </PuikButton>
</template>

<style lang="postcss" scoped>
.ui-button--focus,
.ui-button--focus:focus {
  @apply outline outline-offset-2 outline-2 outline-theme-focus-main;
}

.ui-button--disabled {
  --tw-bg-opacity: 1;
  background-color: rgb(187 187 187 / var(--tw-bg-opacity));
}

.ui-button--tertiary-reverse {
  @apply text-white bg-theme-button-primary-hovered hover:bg-theme-text-captation focus-visible:ring-offset-theme-primary;
}

.ui-tooltip-button {
  @apply w-full h-auto;
  padding: 0 !important;
}
</style>
